import React from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  LinearProgress,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';

import { DISPLAY_FONT, TREND_COLORS } from '../../trendTheme';
import { useTrendAdmin } from '../TrendAdminContext';
import { formatFileSize, formatShortDate } from '../adminHelpers';

export default function ResetDialog() {
  const {
    resetDialogOpen,
    handleCloseResetDialog,
    resetPreview,
    isResetBusy,
    resetSnapshot,
    handleDownloadResetSnapshot,
    resetRequiredText,
    resetConfirmText,
    setResetConfirmText,
    resetRestartPipeline,
    setResetRestartPipeline,
    loadResetPreview,
    busyAction,
    handleCreateResetSnapshot,
    handleHardReset,
  } = useTrendAdmin();

  return (
    <Dialog open={resetDialogOpen} onClose={handleCloseResetDialog} fullWidth maxWidth="md">
      <DialogTitle sx={{ fontFamily: DISPLAY_FONT }}>Beta-reset av trenddata</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Alert severity="warning">
            Återställningen rensar bara genererad trenddata. Källor, discovery-frågor och trendinställningar sparas.
          </Alert>

          {!resetPreview && isResetBusy && <LinearProgress />}

          {resetPreview && (
            <>
              <Box sx={{ p: 2, borderRadius: 2, bgcolor: TREND_COLORS.sectionBg, border: `1px solid ${TREND_COLORS.divider}` }}>
                <Typography sx={{ fontWeight: 700, color: TREND_COLORS.navy, mb: 1 }}>
                  Steg 1: Förhandsvisning av vad som rensas
                </Typography>
                <Grid container spacing={1.5}>
                  {resetPreview.deletableCollections.map((entry) => (
                    <Grid key={entry.key} size={{ xs: 12, sm: 6, md: 4 }}>
                      <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: TREND_COLORS.cardBg, border: `1px solid ${TREND_COLORS.divider}` }}>
                        <Typography variant="caption" sx={{ color: TREND_COLORS.textMuted, display: 'block' }}>
                          {entry.label}
                        </Typography>
                        <Typography sx={{ fontWeight: 700, color: TREND_COLORS.navy }}>
                          {entry.count}
                        </Typography>
                      </Box>
                    </Grid>
                  ))}
                </Grid>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 1.5 }}>
                  <Chip label={`${resetPreview.totalDocumentsToDelete} dokument rensas`} color="error" variant="outlined" />
                  <Chip label={`${resetPreview.totalFilesToDelete} artefaktfiler rensas`} color="warning" variant="outlined" />
                  {resetPreview.artifactFiles.map((entry) => (
                    <Chip key={entry.key} label={`${entry.label}: ${entry.count}`} color="warning" variant="outlined" />
                  ))}
                  {resetPreview.retainedCollections.map((entry) => (
                    <Chip key={entry.key} label={`${entry.label} behålls: ${entry.count}`} variant="outlined" />
                  ))}
                </Stack>
              </Box>

              {!resetPreview.blockingState.canHardReset && (
                <Alert severity="error">
                  {resetPreview.blockingState.blockingReasons.join(' ')}
                </Alert>
              )}

              <Box sx={{ p: 2, borderRadius: 2, bgcolor: TREND_COLORS.sectionBg, border: `1px solid ${TREND_COLORS.divider}` }}>
                <Typography sx={{ fontWeight: 700, color: TREND_COLORS.navy, mb: 0.75 }}>
                  Steg 2: Skapa snapshot/export
                </Typography>
                <Typography variant="body2" sx={{ color: TREND_COLORS.textSecondary, lineHeight: 1.7, mb: 1.5 }}>
                  Snapshot exporterar nuläget till JSON så att du kan spara eller återläsa vad som fanns innan återställningen.
                </Typography>
                {resetSnapshot ? (
                  <Stack spacing={1.25}>
                    <Alert severity="success">
                      Snapshot klart: {resetSnapshot.fileName} ({formatFileSize(resetSnapshot.sizeBytes)}).
                    </Alert>
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25}>
                      <Button
                        variant="outlined"
                        onClick={() => void handleDownloadResetSnapshot(resetSnapshot.snapshotId, resetSnapshot.fileName)}
                      >
                        Ladda ner snapshot
                      </Button>
                      <Typography variant="caption" sx={{ color: TREND_COLORS.textMuted, alignSelf: 'center' }}>
                        Skapat {formatShortDate(resetSnapshot.createdAt)}
                      </Typography>
                    </Stack>
                  </Stack>
                ) : (
                  <Alert severity="info">
                    Inget snapshot skapat ännu. Hård återställning låses upp först efter snapshot.
                  </Alert>
                )}
              </Box>

              <Box sx={{ p: 2, borderRadius: 2, bgcolor: TREND_COLORS.sectionBg, border: `1px solid ${TREND_COLORS.divider}` }}>
                <Typography sx={{ fontWeight: 700, color: TREND_COLORS.navy, mb: 0.75 }}>
                  Steg 3: Bekräftelsetext och valfri omstart
                </Typography>
                <TextField
                  fullWidth
                  size="small"
                  label={`Skriv exakt: ${resetRequiredText}`}
                  value={resetConfirmText}
                  onChange={(event) => setResetConfirmText(event.target.value)}
                  helperText="Bekräftelsen krävs för att undvika felaktig radering."
                  sx={{ mb: 1.5 }}
                />
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ minHeight: 48, px: 1.5, border: `1px solid ${TREND_COLORS.divider}`, borderRadius: 1 }}>
                  <Box>
                    <Typography variant="body2" sx={{ color: TREND_COLORS.textPrimary }}>
                      Starta ny trendkörning direkt
                    </Typography>
                    <Typography variant="caption" sx={{ color: TREND_COLORS.textMuted }}>
                      Bra om du vill att nästa datauppsättning ska börja byggas direkt efter återställningen.
                    </Typography>
                  </Box>
                  <Switch
                    checked={resetRestartPipeline}
                    onChange={(event) => setResetRestartPipeline(event.target.checked)}
                  />
                </Stack>
              </Box>
            </>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleCloseResetDialog}>Avbryt</Button>
        <Button
          variant="outlined"
          disabled={isResetBusy}
          onClick={() => void loadResetPreview()}
        >
          Uppdatera förhandsvisning
        </Button>
        <Button
          variant="outlined"
          color="warning"
          disabled={isResetBusy || !resetPreview?.blockingState.canHardReset}
          onClick={() => void handleCreateResetSnapshot()}
        >
          {busyAction === 'resetSnapshot' ? 'Skapar snapshot...' : 'Skapa snapshot'}
        </Button>
        <Button
          variant="contained"
          color="error"
          disabled={
            isResetBusy
            || !resetPreview?.blockingState.canHardReset
            || !resetSnapshot
            || resetConfirmText.trim() !== resetRequiredText
          }
          onClick={() => void handleHardReset()}
        >
          {busyAction === 'hardReset' ? 'Rensar trenddata...' : 'Hård återställning'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
